import { Controller } from "@hotwired/stimulus";
import { Chart, registerables } from "chart.js";

// Register all Chart.js components we need
Chart.register(...registerables);

export default class extends Controller {
  static values = {
    data: Array,
  };

  connect() {
    console.log("connect");
    if (this.hasDataValue && this.dataValue.length > 0) {
      this.initializeChart();
    }
  }

  initializeChart() {
    // Préparer les données pour Chart.js
    const dates = this.dataValue.map((item) => item[0]); // Première valeur = date
    const prices = this.dataValue.map((item) => item[1]); // Deuxième valeur = prix
    const singlePoint = prices.length === 1;
    const parseLabelToDate = (label) => {
      if (typeof label !== "string") return null;
      const parts = label.split("/");
      if (parts.length !== 3) return null;
      const [ day, month, year ] = parts.map(Number);
      if ([ day, month, year ].some(Number.isNaN)) return null;
      return new Date(year, month - 1, day);
    };

    // Contexte du canvas
    const canvas = this.element.querySelector("canvas");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // Création du graphique avec design amélioré
    this.chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: dates,
        datasets: [
          {
            label: "Prix",
            data: prices,
            borderColor: "#2563eb", // Bleu moderne
            backgroundColor: "transparent", // Pas de remplissage
            borderWidth: 3, // Ligne plus épaisse
            tension: 0.1, // Légère courbure
            pointRadius: singlePoint ? 6 : 0, // Affiche le point si un seul relevé
            pointHoverRadius: singlePoint ? 8 : 6, // Points au survol
            pointHitRadius: singlePoint ? 12 : 8,
            pointBackgroundColor: "#2563eb",
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: "index",
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            borderColor: "#2563eb",
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              title: function (context) {
                const dataPoint = context?.[0];
                if (!dataPoint) return "";
                const parsed = parseLabelToDate(dataPoint.label);
                if (!parsed) return dataPoint.label ?? "";
                return parsed.toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                });
              },
              label: function (context) {
                return new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "EUR",
                }).format(context.raw);
              },
            },
          },
        },
        scales: {
          x: {
            display: true,
            offset: singlePoint, // Centre le point unique sur l'axe X
            grid: {
              display: false, // Pas de grille verticale
            },
            ticks: {
              color: "#6b7280",
              font: {
                size: 11,
              },
              maxRotation: 45,
            },
          },
          y: {
            beginAtZero: true,
            grid: {
              color: "#e5e7eb", // Grille horizontale subtile
              borderDash: [2, 2],
            },
            ticks: {
              color: "#6b7280",
              font: {
                size: 11,
              },
              callback: function (value) {
                return new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "EUR",
                  maximumFractionDigits: 2,
                }).format(value);
              },
            },
          },
        },
        elements: {
          line: {
            borderCapStyle: "round",
            borderJoinStyle: "round",
          },
        },
      },
    });
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
